import React from 'react';

const formatDistance = (pxLength, activeScale) => {
  if (!Number.isFinite(pxLength) || pxLength <= 0) {
    return '';
  }
  const refPx = Number(activeScale?.referencePx);
  const refValue = Number(activeScale?.value);
  if (!Number.isFinite(refPx) || refPx <= 0 || !Number.isFinite(refValue) || refValue <= 0) {
    return `${pxLength.toFixed(1)} px`;
  }
  const converted = (pxLength / refPx) * refValue;
  const unit = String(activeScale?.unit || '').trim();
  const rounded =
    converted >= 100 ? converted.toFixed(0) : converted >= 10 ? converted.toFixed(1) : converted.toFixed(2);
  return unit ? `${rounded} ${unit}` : rounded;
};

const formatDistanceLabels = (pxLength, activeScales = []) => {
  const scales = Array.isArray(activeScales) ? activeScales : [];
  if (!scales.length) {
    return [`${pxLength.toFixed(1)} px`];
  }
  const labels = scales
    .map(scale => {
      const valueLabel = formatDistance(pxLength, scale);
      if (!valueLabel) {
        return null;
      }
      return valueLabel;
    })
    .filter(Boolean);
  return labels.length ? labels : [`${pxLength.toFixed(1)} px`];
};

const sanitizePoints = points =>
  Array.isArray(points)
    ? points.filter(
        point => Number.isFinite(Number(point?.x)) && Number.isFinite(Number(point?.y)),
      )
    : [];

const buildPathFromPoints = (points, offsetX = 0, offsetY = 0) => {
  const safePoints = sanitizePoints(points);
  if (!safePoints.length) {
    return null;
  }
  const [firstPoint, ...restPoints] = safePoints;
  const commands = [`M ${Number(firstPoint.x) + offsetX} ${Number(firstPoint.y) + offsetY}`];
  restPoints.forEach(point => {
    commands.push(`L ${Number(point.x) + offsetX} ${Number(point.y) + offsetY}`);
  });
  if (restPoints.length === 0) {
    commands.push(`L ${Number(firstPoint.x) + offsetX} ${Number(firstPoint.y) + offsetY}`);
  }
  return commands.join(' ');
};

const computePathLength = points => {
  const safePoints = sanitizePoints(points);
  if (safePoints.length < 2) {
    return 0;
  }
  return safePoints.slice(1).reduce((sum, point, index) => {
    const previous = safePoints[index];
    return sum + Math.hypot(Number(point.x) - Number(previous.x), Number(point.y) - Number(previous.y));
  }, 0);
};

export const BoardMapMeasurementsLayer = ({
  measurements = [],
  renderMetrics,
  activeScales = [],
  onDeleteMeasurement,
}) => (
  <>
    {measurements.map(measurement => {
      const x1 = Number(measurement?.x1);
      const y1 = Number(measurement?.y1);
      const x2 = Number(measurement?.x2);
      const y2 = Number(measurement?.y2);
      const rawPoints = sanitizePoints(measurement?.points);
      const fallbackPoints =
        [x1, y1, x2, y2].every(Number.isFinite) ? [{ x: x1, y: y1 }, { x: x2, y: y2 }] : [];
      const points = rawPoints.length >= 2 ? rawPoints : fallbackPoints;
      if (points.length < 2) {
        return null;
      }
      const lengthPx = Number.isFinite(Number(measurement?.length))
        ? Number(measurement.length)
        : computePathLength(points);
      if (lengthPx < 2) {
        return null;
      }
      const labels = formatDistanceLabels(lengthPx, activeScales);
      const titleText = labels.join(' | ');
      const firstPoint = points[0];
      const lastPoint = points[points.length - 1];
      const midX = (Number(firstPoint.x) + Number(lastPoint.x)) / 2 + (Number(renderMetrics?.offsetX) || 0);
      const midY = (Number(firstPoint.y) + Number(lastPoint.y)) / 2 + (Number(renderMetrics?.offsetY) || 0);
      const path = buildPathFromPoints(
        points,
        Number(renderMetrics?.offsetX) || 0,
        Number(renderMetrics?.offsetY) || 0,
      );
      if (!path) {
        return null;
      }
      return (
        <React.Fragment key={measurement?.id || `${firstPoint.x}-${firstPoint.y}-${lastPoint.x}-${lastPoint.y}`}>
          <svg
            className='absolute inset-0'
            width={renderMetrics.canvasWidth}
            height={renderMetrics.canvasHeight}
            viewBox={`0 0 ${renderMetrics.canvasWidth} ${renderMetrics.canvasHeight}`}
            preserveAspectRatio='none'
            style={{ zIndex: 12, pointerEvents: 'none' }}
          >
            <path
              d={path}
              stroke='transparent'
              strokeWidth='14'
              fill='none'
              pointerEvents='stroke'
              style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
              onDoubleClick={event => {
                event.preventDefault();
                event.stopPropagation();
                onDeleteMeasurement?.(measurement?.id);
              }}
            >
              <title>{titleText}</title>
            </path>
            <path
              d={path}
              stroke='rgb(var(--yellow))'
              strokeWidth='2'
              strokeDasharray='6 4'
              strokeOpacity='0.95'
              fill='none'
              pointerEvents='none'
            />
          </svg>
          {labels.length > 0 && (
            <div
              className='absolute z-9997 pointer-events-none -translate-x-1/2 -translate-y-1/2 rounded bg-main bg-opacity-85 px-05 py-px text-xs text-contrast whitespace-nowrap border border-contrast border-opacity-20'
              style={{ left: midX, top: midY }}
            >
              <div className='flex flex-col'>
                {labels.map((value, index) => (
                  <span key={`${value}-${index}`}>{value}</span>
                ))}
              </div>
            </div>
          )}
        </React.Fragment>
      );
    })}
  </>
);
